/** @file The Courses store module to load and update course data with the server. */
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { promise } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as models from '@/models';
import * as utils from '@/utils';
import { AssignmentsStore } from './assignments';
import { SiteSettingsStore } from './siteSettings';
const storeBuilder = getStoreBuilder();
/**
 * Function to get initial state of the store module.
 *
 * @returns The initial state of the store module.
 */
const makeInitialState = () => ({
    courses: { pagination: { tag: 'not-started' } },
    coursesByTenant: {},
    fullCourses: {},
    fullCourseLoaders: {},
});
const moduleBuilder = storeBuilder.module('courses', makeInitialState());
/**
 * Insert a new or updated course into a list of previously loaded courses.
 *
 * This is not as trivial as `push`ing or `unshift`ing the new course, because
 * the list must stay ordered by course creation date.
 *
 * @param old The list of already loaded courses.
 * @param newCourse The course to insert into the list.
 * @returns An updated pagination state including the new course.
 */
function updateCourseList(old, newCourse) {
    const res = [];
    let added = false;
    for (const course of old.data ?? []) {
        if (course.id === newCourse.id) {
            if (!added) {
                res.push(newCourse);
            }
            added = true;
        }
        else {
            if (!added && course.createdAt.isBefore(newCourse.createdAt)) {
                res.push(newCourse);
                added = true;
            }
            res.push(course);
        }
    }
    if (!added && old.pagination.tag === 'finished') {
        res.push(newCourse);
    }
    return { ...old, data: res };
}
/** The Course store module. */
export var CoursesStore;
(function (CoursesStore) {
    /** Get a full course. */
    CoursesStore.getCourse = moduleBuilder.read(state => {
        const lookup = new Map(state.courses.data?.map(course => [course.id, course]));
        return (courseId) => {
            const found = state.fullCourses?.[courseId];
            if (found != null) {
                return Maybe.of(found);
            }
            return Maybe.fromNullable(lookup.get(courseId));
        };
    }, 'getCourse');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.allCourses = moduleBuilder.read(state => utils.getPaginatedState(state.courses), 'allCourses');
    /** Get all courses of a tenant. */
    CoursesStore.tenantCourses = moduleBuilder.read(state => 
    /**
     * Get the courses of a tenant.
     *
     * @param tenantId The id of the tenant to get the courses for.
     * @returns The courses for the requested tenant.
     */
    (tenantId) => utils.getPaginatedState(state.coursesByTenant[tenantId] ?? {
        pagination: { tag: 'not-started' },
    }), 'tenantCourses');
    const commitTenantCourses = moduleBuilder.commit((state, payload) => {
        utils.vueSet(state.coursesByTenant, payload.tenantId, payload.data);
    }, 'commitTenantCourses');
    const commitCourses = moduleBuilder.commit((state, payload) => {
        state.courses = payload;
    }, 'commitCourses');
    const commitClearAll = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearAll');
    /** Add a full course to the store. */
    const commitFullCourse = moduleBuilder.commit((state, { course, }) => {
        utils.vueSet(state.fullCourses, course.id, course);
        state.courses = updateCourseList(state.courses, course);
        if (course.tenantId.isNothing()) {
            return;
        }
        const ofTenant = state.coursesByTenant[course.tenantId.value];
        if (ofTenant != null && ofTenant.data != null) {
            utils.vueSet(state.coursesByTenant, course.tenantId.value, updateCourseList(ofTenant, course));
        }
    }, 'commitFullCourse');
    const commitCourseLoader = moduleBuilder.commit((state, { courseId, loader, }) => {
        if (loader.isJust()) {
            utils.vueSet(state.fullCourseLoaders, courseId, loader.value);
        }
        else {
            utils.vueDelete(state.fullCourseLoaders, courseId);
        }
    }, 'commitCourseLoader');
    /**
     * Update a course's access restrictions in the store without a network
     * round trip.
     *
     * Creates a new frozen {@link models.Course} with the updated restriction
     * and commits it.
     *
     * @param root0 The arguments.
     * @param root0.courseId The course to update.
     * @param root0.restriction The new restriction data from the PATCH response.
     */
    CoursesStore.updateCourseRestriction = moduleBuilder.commit((state, { courseId, restriction, }) => {
        const existing = state.fullCourses[courseId];
        if (existing == null) {
            return;
        }
        const updated = new models.Course(new api.ExtendedCourse({ ...existing, accessRestrictions: restriction }), existing.authorization);
        utils.vueSet(state.fullCourses, courseId, updated);
        state.courses = updateCourseList(state.courses, updated);
    }, 'updateCourseRestriction');
    /** Action to load a single course. */
    CoursesStore.loadSingleCourse = moduleBuilder.dispatch(async ({ state }, 
    /** The payload of the action. */
    { courseId, force = false, courseData, }) => utils.storeLoadWithMaybeForce({
        value: Maybe.fromNullable(state.fullCourses[courseId]),
        loader: Maybe.fromNullable(state.fullCourseLoaders[courseId]),
        addLoader: loader => commitCourseLoader({ courseId, loader: Maybe.of(loader) }),
        removeLoader: () => commitCourseLoader({ courseId, loader: Maybe.empty() }),
        force,
        load: async () => {
            if (courseData != null) {
                const [{ body: permissions }] = await Promise.all([
                    client.p.course.getAuthorization({ courseId }),
                    SiteSettingsStore.loadSettings({
                        tenantId: courseData.tenantId,
                    }),
                ]);
                commitFullCourse({
                    course: new models.Course(courseData, permissions),
                });
                return;
            }
            const foundCourse = CoursesStore.getCourse()(courseId);
            if (foundCourse.isJust()) {
                // If we already have a course (maybe partial) we try to already start loading the settings.
                promise.preload(SiteSettingsStore.loadSettings({
                    tenantId: foundCourse.value.tenantId,
                }).catch(() => { }));
            }
            const [{ body: course }, { body: permissions }] = await Promise.all([
                client.p.course.get({ courseId }).then(async (resp) => {
                    await SiteSettingsStore.loadSettings({
                        tenantId: resp.cgResult.tenantId,
                    });
                    return resp;
                }),
                client.p.course.getAuthorization({ courseId }),
            ]);
            commitFullCourse({
                course: new models.Course(course, permissions),
            });
        },
    }), 'loadSingleCourse');
    /**
     * Load the list of courses.
     *
     * @param payload The arguments to this dispatch.
     * @returns A Promise that resolves when the request has finished.
     */
    CoursesStore.loadCourses = moduleBuilder.dispatch(async ({ state }, { toLoad, }) => {
        await utils.loadPaginatedWithForce({
            getData: () => state.courses,
            load: nextToken => client.p.course.getAll({
                nextToken,
            }),
            commitData: commitCourses,
            toLoad,
        });
    }, 'loadFirstCourses');
    /**
     * Load the courses of a tenant.
     *
     * @param payload The arguments to this dispatch.
     * @returns A Promise that resolves when the request has finished.
     */
    CoursesStore.loadTenantCourses = moduleBuilder.dispatch(async ({ state }, { tenantId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            getData: () => state.coursesByTenant[tenantId],
            load: nextToken => client.p.course.getAll({ nextToken, tenantId }),
            commitData: data => commitTenantCourses({ data, tenantId }),
            toLoad,
        });
    }, 'loadTenantCourses');
    const updateCourse = moduleBuilder.dispatch(async ({ state }, { course, }) => {
        const loader = state.fullCourseLoaders[course.id];
        if (loader != null) {
            await loader;
        }
        let auth = state.fullCourses[course.id]?.authorization;
        if (auth == null) {
            [{ body: auth }] = await Promise.all([
                client.p.course.getAuthorization({
                    courseId: course.id,
                }),
                SiteSettingsStore.loadSettings({ tenantId: course.tenantId }),
            ]);
        }
        else {
            await SiteSettingsStore.loadSettings({ tenantId: course.tenantId });
        }
        commitFullCourse({
            course: new models.Course(course, auth),
        });
    }, 'updateCourse');
    /** Update basic course information. */
    CoursesStore.patchCourse = moduleBuilder.dispatch(async ({ state }, { courseId, data, }) => {
        const loader = state.fullCourseLoaders[courseId];
        if (loader != null) {
            await loader;
        }
        const response = await client.p.course.patch({ courseId, requestBody: data });
        await updateCourse({ course: response.body });
        return response.withoutBody();
    }, 'patchCourse');
    /** Create a new course. */
    CoursesStore.createCourse = moduleBuilder.dispatch(async (_, payload) => {
        const response = await client.p.course.create({ requestBody: payload });
        await updateCourse({ course: response.body });
        return response;
    }, 'createCourse');
    /** Update the price of a course. */
    CoursesStore.putCoursePrice = moduleBuilder.dispatch(async (_, { courseId, data, }) => {
        const response = await client.p.course.putPrice({
            courseId,
            requestBody: data,
        });
        return response.withoutBody().addCallbacks({
            onAfterSuccess: async ({ cgResult: price }) => {
                const course = CoursesStore.getCourse()(courseId).extract();
                if (!(course instanceof models.Course)) {
                    return;
                }
                await updateCourse({
                    course: {
                        ...course,
                        price: Maybe.of(price),
                    },
                });
            },
        });
    }, 'putCoursePrice');
    /** Delete the price of a course. */
    CoursesStore.deleteCoursePrice = moduleBuilder.dispatch(async (_, { courseId, }) => {
        const response = await client.p.course.deletePrice({ courseId });
        return response.withoutBody().addCallbacks({
            onAfterSuccess: async () => {
                const course = CoursesStore.getCourse()(courseId).extract();
                if (!(course instanceof models.Course)) {
                    return;
                }
                await updateCourse({
                    course: {
                        ...course,
                        price: Maybe.empty(),
                    },
                });
            },
        });
    }, 'deleteCoursePrice');
    /** Action to copy a course to a different course. */
    CoursesStore.copyCourse = moduleBuilder.dispatch(async (_, payload) => {
        const response = await client.p.course.importInto({
            intoCourseId: payload.into.id,
            requestBody: {
                fromCourseId: payload.from.id,
            },
        });
        const course = response.body;
        await updateCourse({ course });
        AssignmentsStore.commitCourseAssignments({
            courseId: course.id,
            assignments: { pagination: { tag: 'not-started' } },
        });
        return response;
    }, 'copyCourse');
    utils.signal.AFTER_USER_CHANGED.registerSync(() => {
        commitClearAll();
    });
})(CoursesStore || (CoursesStore = {}));
